// Main App Component with Advanced React Architecture
// React Patterns: Routing + Error Boundaries + Lazy Loading + Context + Performance

import React, { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { AppProvider } from './store/AppContext';
import ErrorBoundary from './components/ui/ErrorBoundary';
import Sidebar from './layouts/Sidebar';
import Header from './layouts/Header';
import { useUI } from './store/AppContext';
import { configureAdminRealtime, disconnectAdminRealtime, getAdminRealtimeSocket } from './services/realtime';
import ApiService from './services/api';
import CaptchaWidget from './components/security/CaptchaWidget';
import {
  normalizeAdminUser,
  pickDefaultStaffRow,
  resolveUserPermissions,
  sessionUserFromStaffRow,
} from './security/acl';
import { DisplayPreferencesProvider } from './context/DisplayPreferencesContext';
import AdminAppRoutes from './navigation/AdminAppRoutes';
import AdminLocationSync from './navigation/AdminLocationSync';
import {
  clearAdminStaffTokenCookie,
  syncAdminStaffTokenCookie,
} from './utils/adminStaffTokenCookie';

const SESSION_STAFF_USER_ID_KEY = 'admin-staff-user-id';

function safeReturnPath(raw) {
  const path = String(raw || '').trim();
  if (!path.startsWith('/') || path.startsWith('//')) return null;
  return path;
}

function redirectAfterStaffAuth() {
  try {
    const returnTo = safeReturnPath(new URLSearchParams(window.location.search).get('return'));
    if (returnTo) window.location.assign(returnTo);
  } catch {
    /* ignore */
  }
}

const ADMIN_MENU_ORDER = [
  'dashboard',
  'watchtower',
  'bookings',
  'booking-view',
  'booking-edit',
  'booking-activity',
  'booking-invoices',
  'booking-transactions',
  'booking-manual-broadcast',
  'guest-vendor-interests',
  'vendor-cab-availability',
  'booking-leads',
  'booking-quotes',
  'broadcasts',
  'vendors',
  'vendor-join-us-enquiries',
  'pan-india-vendors',
  'vendor-notifications',
  'vendor-penalties',
  'finance-dashboard',

  'finance-vehicle-ledger',
  'finance-expenses',
  'finance-fastag-import',
  'finance-zoho-export',
  'finance-driver-ledger',
  'finance-vendor-finance',
  'finance-vendor-settlement',
  'finance-fleet-utilization',
  'finance-driver-performance',
  'finance-gst-summary',
  'finance-vehicle-pl',
  'finance-vendor-payouts',
  'finance-driver-settlements',
  'finance-toll-fuel',
  'finance-gst-invoices',
  'finance-customer-payments',
  'finance-fleet-profitability',
  'finance-emi-loans',
  'finance-route-pl',
  'finance-multi-vendor',
  'finance-reconciliation',
  'cabs-fleet',
  'cabs-approval',
  'cab-expenses',
  'cab-revenue',
  'cab-sold-out',
  'drivers-roster',
  'driver-approval',
  'driver-salary-slip',
  'driver-expenses',
  'pricing',
  'pricing-vendor-pricing',
  'pricing-price-analysis',
  'cab-tour-packages',
  'jyotirlinga-engine',
  'ashtavinayak-tours',
  'ashtavinayak-temples',
  'pilgrimage-sites',
  'cab-package-enquiries',
  'cab-package-user-logged',
  'route-page-user-logged',
  'vehicle-landing-user-logged',
  'tours-packages-settings',
  'nearby-places',
  'business-presence-dashboard',
  'business-presence-locations',
  'business-presence-posts',
  'business-presence-reviews',
  'business-presence-accounts',
  'business-presence-seo',
  'business-presence-settings',
  'routes',
  'distance-calculator',
  'cities',
  'location-missing-searches',
  'location-search-index',
  'location-master-ops',
  'route-pages',
  'seo-control-center',
  'seo-page-urls',
  'sitemaps',
  'robots-txt',
  'seo-settings',
  'seo-group',
  'checkout-addons',
  'snack-box-dashboard',
  'snack-boxes',
  'snack-box-inventory',
  'snack-box-orders',
  'snack-box-reports',
  'snack-box-settings',
  'checkout-online-payment-options',
  'checkout-terms-conditions',
  'cms-route-pages',
  'cms-route-pages-editor',
  'cms-route-pages-css',
  'homepage-popular-routes',
  'homepage-popular-route-categories',
  'homepage-header',
  'homepage-hero-banner',
  'homepage-footer',
  'homepage-not-found',
  'homepage-sections',
  'gallery-share-settings',
  'faq-categories',
  'faqs',
  'blog-posts',
  'blog-posts-editor',
  'blog-categories',
  'blog-tags',
  'blog-comments',
  'reviews',
  'reviews-group',
  'review-automation-dashboard',
  'review-automation-requests',
  'review-automation-reports',
  'review-automation-settings',
  'contact-messages',
  'contact-form-settings',
  'segment-configuration',
  'users',
  'customer-activities-log',
  'corporate-clients',
  'corporate-pending',
  'corporate-bookings',
  'corporate-pricing',
  'corporate-contracts',
  'corporate-invoices',
  'corporate-reports',
  'corporate-erp',
  'corporate-client-detail',
  'customer-view',
  'customer-edit',
  'analytics',
  'analytics-cancellations',
  'analytics-coupon-usage',
  'analytics-cookie-consent',
  'marketing-leads',
  'utm-tracking-dashboard',
  'utm-tracking-urls',
  'newsletter-subscribers',
  'newsletter-campaigns',
  'newsletter-settings',
  'marketing-campaigns',
  'marketing-sources',
  'marketing-conversion',
  'marketing-revenue',
  'marketing-journey',
  'brand-ads-dashboard',
  'brand-ads-campaigns',
  'brand-ads-advertisers',
  'brand-ads-placements',
  'brand-ads-landing',
  'brand-ads-reports',
  'brand-ads-settings',
  'logs',
  'qa-test-results',
  'dev-doc',
  'db-backup',
  'settings',
  'maintenance-settings',
  'database-settings',
  'mobile-app-settings',
  'website-design-settings',
  'website-schema-settings',
  'driver-app-settings',
  'loyalty-settings',
  'chatbot-settings',
  'status-management',
  'notification-center',
  'profile-settings',
  'change-password',
  'acl-all-users',
  'acl-locked-users',
  'acl-user-roles',
  'acl-admin-access-log',
  'ai-route-manager',
];


/** Shown after explicit sign-out; staff sign-in via BFF `POST /admin/staff-login`. */
function SignedOutScreen({ onSignedIn }) {
  const { setCurrentUser } = useUI();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [captchaProvider, setCaptchaProvider] = useState('none');
  const [turnstileSiteKey, setTurnstileSiteKey] = useState('');
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState('');
  const [recaptchaType, setRecaptchaType] = useState('v2_checkbox');
  const [captchaEnabled, setCaptchaEnabled] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await ApiService.bffRequest('/pricing/public/system-settings');
        if (cancelled || !res?.success || !res.data) return;
        const map =
          res.data && typeof res.data === 'object' && !Array.isArray(res.data)
            ? res.data
            : {};
        if (Array.isArray(res.data)) {
          for (const row of res.data) {
            if (row?.key) map[row.key] = row.value;
          }
        }
        try {
          const { setPublicCustomerSiteUrl } = await import('./utils/publicSiteUrl');
          setPublicCustomerSiteUrl(map.customer_site_url || map.company_website || '');
        } catch {
          /* optional */
        }
        const turnstileLegacy = ['true', '1', 'yes', 'on'].includes(
          String(map.security_turnstile_enabled || '').toLowerCase(),
        );
        let provider = String(map.security_captcha_provider || '').trim().toLowerCase();
        if (!['none', 'turnstile', 'recaptcha'].includes(provider)) {
          provider = turnstileLegacy ? 'turnstile' : 'none';
        }
        const tsKey = String(map.security_turnstile_site_key || '').trim();
        const rcKey = String(map.security_recaptcha_site_key || '').trim();
        let rcType = String(map.security_recaptcha_type || '').trim().toLowerCase();
        if (!['v2_checkbox', 'v2_invisible', 'v3'].includes(rcType)) {
          rcType = 'v2_checkbox';
        }
        setCaptchaProvider(provider);
        setTurnstileSiteKey(tsKey);
        setRecaptchaSiteKey(rcKey);
        setRecaptchaType(rcType);
        setCaptchaEnabled(
          (provider === 'turnstile' && tsKey.length > 0) ||
            (provider === 'recaptcha' && rcKey.length > 0),
        );
      } catch {
        /* optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const postLogoutUrl =
    typeof import.meta !== 'undefined' && import.meta.env?.VITE_ADMIN_POST_LOGOUT_URL
      ? String(import.meta.env.VITE_ADMIN_POST_LOGOUT_URL).trim()
      : '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const em = String(email || '').trim().toLowerCase();
    if (!em || !password) {
      setError('Enter your email and password.');
      return;
    }
    if (captchaEnabled && !captchaToken) {
      setError('Please complete the security check.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await ApiService.bffStaffLogin({
        email: em,
        password,
        captcha_token: captchaEnabled ? captchaToken : undefined,
      });
      if (!res?.success || !res?.data?.user) {
        setError(res?.message || 'Sign-in failed.');
        return;
      }
      const { user, token } = res.data;
      const staffToken = res.data?.legacy_jwt || token || res.data?.access_token || res.access_token;
      localStorage.removeItem('admin-signed-out');
      try {
        sessionStorage.removeItem('admin-staff-jwt');
        if (staffToken) sessionStorage.setItem('admin-staff-jwt', staffToken);
        if (user?.id != null) sessionStorage.setItem(SESSION_STAFF_USER_ID_KEY, String(user.id));
        syncAdminStaffTokenCookie(staffToken);
      } catch {
        /* ignore storage errors */
      }
      setCurrentUser(normalizeAdminUser(user));
      onSignedIn?.();
      redirectAfterStaffAuth();
    } catch (err) {
      setError(err?.message || 'Sign-in failed. Check email, password, and API connection.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-6">
      <div className="max-w-md w-full rounded-2xl border border-slate-200 bg-white p-8 shadow-lg">
        <div className="text-center mb-6">
          <div className="text-lg font-semibold text-slate-900 mb-2">You have been signed out</div>
          <p className="text-sm text-slate-600">Sign in with your admin account to continue.</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 text-left">
          <div>
            <label htmlFor="admin-signin-email" className="block text-sm font-medium text-slate-700 mb-1">
              Email
            </label>
            <input
              id="admin-signin-email"
              data-testid="admin-signin-email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="admin@example.com"
              disabled={submitting}
            />
          </div>
          <div>
            <label htmlFor="admin-signin-password" className="block text-sm font-medium text-slate-700 mb-1">
              Password
            </label>
            <input
              id="admin-signin-password"
              data-testid="admin-signin-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(ev) => setPassword(ev.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="••••••••"
              disabled={submitting}
            />
          </div>
          <input
            type="text"
            name="_hp"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="absolute opacity-0 pointer-events-none h-0 w-0 overflow-hidden"
            defaultValue=""
          />
          {captchaEnabled ? (
            <CaptchaWidget
              provider={captchaProvider}
              turnstileSiteKey={turnstileSiteKey}
              recaptchaSiteKey={recaptchaSiteKey}
              recaptchaType={recaptchaType}
              action="admin_login"
              onToken={setCaptchaToken}
              onExpire={() => setCaptchaToken('')}
              className="flex justify-center"
            />
          ) : null}
          {error ? (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800" role="alert">
              {error}
            </div>
          ) : null}
          <button
            type="submit"
            data-testid="admin-signin-submit"
            disabled={submitting}
            className="w-full rounded-xl bg-indigo-600 px-4 py-3 text-white font-medium hover:bg-indigo-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        {postLogoutUrl ? (
          <div className="mt-4 text-center">
            <a
              href={postLogoutUrl}
              className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
            >
              Go to site
            </a>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// 🎯 Main Layout Component
const MainLayout = () => {
  const { sidebar, theme, setTheme, addNotification, setCurrentUser, currentUser } = useUI();
  const playAdminAlert = () => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.value = 780;
    gain.gain.value = 0.03;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    setTimeout(() => {
      osc.stop();
      ctx.close().catch(() => {});
    }, 120);
  };

  // 🌙 Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = String(localStorage.getItem('admin-theme') || '').toLowerCase();
    const nextTheme = savedTheme === 'dark' ? 'dark' : 'light';
    setTheme(nextTheme);
  }, [setTheme]);

  useEffect(() => {
    ['admin-acl-user', 'admin-acl-users', 'admin-acl-roles'].forEach((k) => localStorage.removeItem(k));
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let jwt = null;
        try {
          jwt = sessionStorage.getItem('admin-staff-jwt');
        } catch {
          jwt = null;
        }
        if (!jwt) {
          if (!cancelled) {
            clearAdminStaffTokenCookie();
            localStorage.setItem('admin-signed-out', '1');
            window.dispatchEvent(new CustomEvent('admin:session-signed-out'));
          }
          return;
        }
        syncAdminStaffTokenCookie(jwt);
        const res = await ApiService.bffListStaffUsers({ limit: 2000 });
        const rows = Array.isArray(res?.data) ? res.data : [];
        if (cancelled) return;
        const persistedId = (() => {
          try {
            const raw = sessionStorage.getItem(SESSION_STAFF_USER_ID_KEY);
            const parsed = parseInt(String(raw || ''), 10);
            return Number.isFinite(parsed) ? parsed : null;
          } catch {
            return null;
          }
        })();
        const existingId = currentUser?.id;
        const sessionId = persistedId != null ? persistedId : (typeof existingId === 'number' ? existingId : null);
        if (sessionId != null && rows.some((r) => r.id === sessionId)) {
          const row = rows.find((r) => r.id === sessionId);
          try {
            sessionStorage.setItem(SESSION_STAFF_USER_ID_KEY, String(sessionId));
          } catch {
            /* ignore */
          }
          setCurrentUser(sessionUserFromStaffRow(row));
          return;
        }
        const pick = pickDefaultStaffRow(rows);
        try {
          if (pick?.id != null) sessionStorage.setItem(SESSION_STAFF_USER_ID_KEY, String(pick.id));
        } catch {
          /* ignore */
        }
        setCurrentUser(pick ? sessionUserFromStaffRow(pick) : normalizeAdminUser(null));
      } catch (err) {
        if (cancelled) return;
        const msg = String(err?.message || '');
        if (msg.includes('UNAUTHORIZED') || msg.toLowerCase().includes('authentication required')) {
          ApiService.clearAdminSession();
          return;
        }
        setCurrentUser(normalizeAdminUser(null));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setCurrentUser, currentUser?.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await ApiService.bffRequest('/pricing/public/system-settings');
        if (cancelled || !res?.success || !res.data) return;
        const map =
          res.data && typeof res.data === 'object' && !Array.isArray(res.data)
            ? { ...res.data }
            : {};
        if (Array.isArray(res.data)) {
          for (const row of res.data) {
            if (row?.key) map[row.key] = row.value;
          }
        }
        const { setPublicCustomerSiteUrl } = await import('./utils/publicSiteUrl');
        if (!cancelled) {
          setPublicCustomerSiteUrl(map.customer_site_url || map.company_website || '');
        }
      } catch {
        /* optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.id]);

  // 🎨 Apply theme to body
  useEffect(() => {
    document.body.className = theme === 'dark' ? 'dark-theme' : 'light-theme';
    document.documentElement.classList.toggle('dark-theme', theme === 'dark');
    localStorage.setItem('admin-theme', theme);
  }, [theme]);

  const realtimeSessionKey = `${currentUser?.id ?? ''}:${currentUser?.role ?? ''}:${resolveUserPermissions(currentUser).join(',')}`;

  useEffect(() => {
    let jwt = null;
    try {
      jwt = sessionStorage.getItem('admin-staff-jwt');
    } catch {
      jwt = null;
    }
    configureAdminRealtime({ user: currentUser, token: jwt });
  }, [realtimeSessionKey, currentUser]); // currentUser: latest object for configureAdminRealtime

  useEffect(() => {
    const socket = getAdminRealtimeSocket();
    if (!socket) return undefined;

    const onConnect = () => {
      window.dispatchEvent(new CustomEvent('admin:realtime-status', { detail: { status: 'connected' } }));
    };
    const onConnectError = () => {
      window.dispatchEvent(new CustomEvent('admin:realtime-status', { detail: { status: 'error' } }));
    };
    const onDisconnect = () => {
      window.dispatchEvent(new CustomEvent('admin:realtime-status', { detail: { status: 'disconnected' } }));
    };
    const onNew = (payload = {}) => {
      addNotification({
        title: 'New booking broadcast',
        message: `Booking #${payload.booking_id || '—'} sent to vendors`,
        type: 'booking',
        read: false,
      });
      playAdminAlert();
      window.dispatchEvent(new CustomEvent('admin:broadcast-event', { detail: { event: 'NEW_BOOKING', payload } }));
    };
    const onBid = (payload = {}) => {
      addNotification({
        title: 'Bid submitted',
        message: `Vendor ${payload.vendor_id || '—'} responded for booking #${payload.booking_id || '—'}`,
        type: 'vendor',
        read: false,
      });
      playAdminAlert();
      window.dispatchEvent(new CustomEvent('admin:broadcast-event', { detail: { event: 'BID_SUBMITTED', payload } }));
    };
    const onAssigned = (payload = {}) => {
      addNotification({
        title: 'Booking assigned',
        message: `Booking #${payload.booking_id || '—'} assigned to vendor ${payload.vendor_id || '—'}`,
        type: 'booking',
        read: false,
      });
      playAdminAlert();
      window.dispatchEvent(new CustomEvent('admin:broadcast-event', { detail: { event: 'BOOKING_ASSIGNED', payload } }));
    };
    const onDriverAssigned = (payload = {}) => {
      const ref = payload.booking_number || payload.booking_id || '—';
      const driverLabel = payload.driver_name || (payload.driver_id ? `Driver #${payload.driver_id}` : 'driver');
      addNotification({
        title: 'Driver assigned',
        message: `Booking ${ref}: ${driverLabel} assigned${payload.vendor_id ? ` by vendor #${payload.vendor_id}` : ''}`,
        type: 'booking',
        read: false,
      });
      playAdminAlert();
      window.dispatchEvent(new CustomEvent('admin:booking-event', { detail: { event: 'DRIVER_ASSIGNED', payload } }));
    };
    const onDriverArrived = (payload = {}) => {
      const ref = payload.booking_number || payload.booking_id || '—';
      addNotification({
        title: 'Driver arrived',
        message: `Driver reached pickup for booking ${ref}`,
        type: 'booking',
        read: false,
      });
      playAdminAlert();
      window.dispatchEvent(new CustomEvent('admin:booking-event', { detail: { event: 'DRIVER_ARRIVED', payload } }));
    };
    const onTripStarted = (payload = {}) => {
      const ref = payload.booking_number || payload.booking_id || '—';
      addNotification({
        title: 'Trip started',
        message: `Trip in progress for booking ${ref}`,
        type: 'booking',
        read: false,
      });
      playAdminAlert();
      window.dispatchEvent(new CustomEvent('admin:booking-event', { detail: { event: 'TRIP_STARTED', payload } }));
    };
    const onTripCompleted = (payload = {}) => {
      const ref = payload.booking_number || payload.booking_id || '—';
      addNotification({
        title: 'Trip completed',
        message: `Booking ${ref} marked completed`,
        type: 'booking',
        read: false,
      });
      playAdminAlert();
      window.dispatchEvent(new CustomEvent('admin:booking-event', { detail: { event: 'TRIP_COMPLETED', payload } }));
    };
    const onBookingCancelled = (payload = {}) => {
      const ref = payload.booking_number || payload.booking_id || '—';
      addNotification({
        title: 'Booking cancelled',
        message: `Booking ${ref} was cancelled`,
        type: 'booking',
        read: false,
      });
      playAdminAlert();
      window.dispatchEvent(new CustomEvent('admin:booking-event', { detail: { event: 'BOOKING_CANCELLED', payload } }));
    };
    const onExpired = (payload = {}) => {
      addNotification({
        title: 'Broadcast expired',
        message: `Booking #${payload.booking_id || '—'} ended without assignment`,
        type: 'system',
        read: false,
      });
      playAdminAlert();
      window.dispatchEvent(new CustomEvent('admin:broadcast-event', { detail: { event: 'BOOKING_EXPIRED', payload } }));
    };

    socket.on('NEW_BOOKING', onNew);
    socket.on('BID_SUBMITTED', onBid);
    socket.on('BOOKING_ASSIGNED', onAssigned);
    socket.on('DRIVER_ASSIGNED', onDriverAssigned);
    socket.on('DRIVER_ARRIVED', onDriverArrived);
    socket.on('TRIP_STARTED', onTripStarted);
    socket.on('TRIP_COMPLETED', onTripCompleted);
    socket.on('BOOKING_CANCELLED', onBookingCancelled);
    socket.on('BOOKING_EXPIRED', onExpired);
    socket.on('connect', onConnect);
    socket.on('connect_error', onConnectError);
    socket.on('disconnect', onDisconnect);

    return () => {
      socket.off('NEW_BOOKING', onNew);
      socket.off('BID_SUBMITTED', onBid);
      socket.off('BOOKING_ASSIGNED', onAssigned);
      socket.off('DRIVER_ASSIGNED', onDriverAssigned);
      socket.off('DRIVER_ARRIVED', onDriverArrived);
      socket.off('TRIP_STARTED', onTripStarted);
      socket.off('TRIP_COMPLETED', onTripCompleted);
      socket.off('BOOKING_CANCELLED', onBookingCancelled);
      socket.off('BOOKING_EXPIRED', onExpired);
      socket.off('connect', onConnect);
      socket.off('connect_error', onConnectError);
      socket.off('disconnect', onDisconnect);
    };
  }, [addNotification, realtimeSessionKey]);

  return (
    <DisplayPreferencesProvider>
    <div className={`min-h-screen bg-slate-50 ${theme === 'dark' ? 'dark' : ''}`}>
      {/* 🗂️ Sidebar */}
      <Sidebar />

      {/* 📱 Main Content Area */}
      <div className={`transition-all duration-300 ${sidebar.isOpen ? 'lg:ml-64' : 'lg:ml-16'
        }`}>
        {/* 📊 Header */}
        <Header />

        <AdminLocationSync menuOrder={ADMIN_MENU_ORDER} />

        {/* 📄 Page Content */}
        <main className="min-h-screen pt-20">
          <AdminAppRoutes />
        </main>
      </div>

      {/* 🌚 Overlay for mobile */}
      {sidebar.isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => {/* Close sidebar on mobile */ }}
        />
      )}
    </div>
    </DisplayPreferencesProvider>
  );
};

function AppShell() {
  const [signedOut, setSignedOut] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (localStorage.getItem('admin-signed-out') === '1') return true;
    try {
      return !sessionStorage.getItem('admin-staff-jwt');
    } catch {
      return true;
    }
  });

  useEffect(() => {
    const onSessionSignedOut = () => setSignedOut(true);
    window.addEventListener('admin:session-signed-out', onSessionSignedOut);
    return () => window.removeEventListener('admin:session-signed-out', onSessionSignedOut);
  }, []);

  useEffect(() => {
    if (signedOut) return;
    redirectAfterStaffAuth();
  }, [signedOut]);

  return (
    <div className="App">
      {signedOut ? (
        <SignedOutScreen onSignedIn={() => setSignedOut(false)} />
      ) : (
        <MainLayout />
      )}
    </div>
  );
}

// 🎯 Main App Component
const App = () => (
  <ErrorBoundary>
    <AppProvider>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </AppProvider>
  </ErrorBoundary>
);

export default App;